// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState } from 'react';
import { Collapse, IconButton, Typography, ListItemButton, Button, Popover } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import GestureIcon from '@mui/icons-material/Gesture';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import HelpIcon from '@mui/icons-material/Help';
import SVG from 'react-inlinesvg';
import downloadIcon from 'Assets/download-icon.png';
import uploadKmlIcon from 'Assets/upload-kml-icon.svg';
import roadNetworkIcon from 'Assets/road-network-icon.png';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { WideLightTooltip } from 'Components/Tooltip';
import ConfirmDialog from 'Components/ConfirmDialog';
import ConfirmProceedDialog from 'Components/ConfirmProceedDialog';
import { getOrders } from 'Features/order/orderSelectors';
import { getRasterRequestStatus } from 'Features/project/projectSelectors';
import { SetCreateKmlAction} from 'Features/map/mapDrawActions';
import { Clear, StartKmlDraw, CancelDraw } from 'Features/map/mapDrawThunk';
import { SetShowKmlToolbarAction } from 'Features/project/projectActions';
import { GetAvailableRoadTypesStartAction, ResetBufferStateAction, ToggleKmlVisibilityAction } from 'Features/kml/kmlActions';
import { ResetImageryCoverageAction } from 'Features/imagery/imageryActions';
import { DownloadKMLThunk,GetAvailableRoadTypesThunk,SetRoadNetworkThunk } from 'Features/kml/kmlThunk';
import { ShowUploadSingleFileDialogAction } from 'Features/fileUpload/fileUploadActions';
import ErrorIcon from '@mui/icons-material/Error';
import stylesDef from './styles';

const KmlPanel = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const orders = useAppSelector((state) => getOrders(state));

  const { nonActiveOrders, activeOrder } = orders;
  let activeOrderKml: { id: any; name: any; isEstimate: any; deliveryDays: any; geomType?: string, kmlMismatch?: boolean };
  if (activeOrder.boundaryFile) {
    activeOrderKml = {
      id: activeOrder._id,
      name: activeOrder.boundaryFile.split('/').pop(),
      isEstimate: activeOrder.isEstimate,
      deliveryDays: activeOrder.deliveryDays,
      geomType: activeOrder.geomType,
      kmlMismatch: activeOrder.kmlMismatch,
    };
  }

  const activeKmlUrl = activeOrder.boundaryFile;
  const { isEstimate } = activeOrder;
  const kmls = useAppSelector((state) => state.kml.present.kmls);
  const mode = useAppSelector((state) => state.map.draw.present.mode);
  const showBusinessDays = useAppSelector((state) => state.kml.present.showBusinessDays);

  const nonActiveOrdersKmls = nonActiveOrders
    .filter((o) => o.boundaryFile)
    .map((o) => {
      const kml = kmls[o._id];
      return {
        id: o._id,
        name: o.boundaryFile.split('/').pop(),
        kmlMismatch: o.kmlMismatch,
      };
    });

  const currentProject = useAppSelector((state) => state.project.project);
  const imageryRequested = (activeOrder?.isEstimate && !!(currentProject?.imageryRequest?.requested)) || !!(currentProject?.imageryRequest?.uploaded) || !!(currentProject?.imageryRequest?.notificationSent);
  const rasterRequest = useAppSelector((state) => getRasterRequestStatus(state));
  const rasterizationDisable = rasterRequest.requested || rasterRequest.rejected;
  const [kmlExpanded, setKmlExpanded] = useState(true);
  const toggleKmlExpanded = () => {
    setKmlExpanded(!kmlExpanded);
  };

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [onDialogOk, setOnDialogOk] = useState(null);
  const [showConfirmProceedDialog, setShowConfirmProceedDialog] = useState(false);
  const [showKmlMismatchPopover, setShowKmlMismatchPopover] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const hasExistingKml = kmls[orders.activeOrder._id]?.featureCollection.features.length > 0;

  const drawKmlClick = () => {
    // reset all imagery checks when drawing a new kml
    // this is to force the user to check the imagery again after drawing a new kml
    // and set all imagery checks to false
    dispatch(ResetImageryCoverageAction());
    if (hasExistingKml) {
      if (isEstimate) {
        setShowConfirmProceedDialog(true);
      } else {
        setShowConfirmDialog(true);
      }
      setOnDialogOk('startDraw');
    } else {
      dispatch(SetCreateKmlAction(true));
      dispatch(SetShowKmlToolbarAction(true));
      dispatch(StartKmlDraw());
      dispatch(ResetBufferStateAction());
    }
  };

  const uploadKmlClick = (e: React.ChangeEvent<{}>) => {
    // reset all imagery checks when uploading a new kml
    // this is to force the user to check the imagery again after uploading a new kml
    // and set all imagery checks to false
    dispatch(ResetImageryCoverageAction());
    if (hasExistingKml) {
      setShowConfirmDialog(true);
      setOnDialogOk('showUploadKml');
    } else {
      dispatch(CancelDraw());
      dispatch(SetShowKmlToolbarAction(false));
      dispatch(ShowUploadSingleFileDialogAction());
      dispatch(ResetBufferStateAction());
    }
    e.stopPropagation();
  };

  const onOk = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setShowConfirmDialog(false);
    setShowConfirmProceedDialog(false);
    dispatch(Clear());
    if (onDialogOk === 'startDraw') {
      dispatch(SetCreateKmlAction(true));
      dispatch(SetShowKmlToolbarAction(true));
      dispatch(StartKmlDraw());
    } else if (onDialogOk === 'showUploadKml') {
      dispatch(CancelDraw());
      dispatch(SetShowKmlToolbarAction(false));
      dispatch(ShowUploadSingleFileDialogAction());
    }
    dispatch(ResetBufferStateAction());
    e.stopPropagation();
  };

  const onCancel = () => {
    setShowConfirmDialog(false);
  };

  const KmlDownloadClick = async () => {
    await dispatch(DownloadKMLThunk(activeOrderKml.id, activeOrderKml.name));
  };

  let tabValue: boolean | 'draw' | 'upload' = false;
  if (mode && mode.name === 'draw_polygon') {
    tabValue = 'draw';
  }
  if (showConfirmDialog) {
    tabValue = 'upload';
  }

  const handleGenerateRoadNetwork = async () => {
    dispatch(GetAvailableRoadTypesStartAction(true));
    dispatch(SetRoadNetworkThunk(true));
    await dispatch(GetAvailableRoadTypesThunk());
  }

  const renderBusinessDays = () => {
    if (!activeOrderKml?.isEstimate || !showBusinessDays) return null;
    if (activeOrderKml.deliveryDays > 20) {
      return (
        <Box sx={styles.businessDays}>
          <Typography variant="body2" sx={styles.businessDaysText}>
            This project is eligible for super expedited delivery
          </Typography>
          <WideLightTooltip
            placement="bottom"
            title={(
              <Box>
                Need this back super fast? Our team can provide a custom delivery date for this project. Reach out to
                {' '}
                <a href="mailto:sales@airworks.io">sales@airworks.io</a>
                {' '}
                to get a quote.
              </Box>
            )}
          >
            <HelpIcon sx={styles.helpIcon} />
          </WideLightTooltip>
        </Box>
      );
    }
    return (
      <Box sx={styles.businessDays}>
        <Typography variant="body2" sx={styles.businessDaysText}>
          {activeOrderKml.deliveryDays}
          {' '}
          Business Days
        </Typography>
        <WideLightTooltip
          placement="bottom"
          title={(
            <Box>
              The actual delivery date of your project is subject to change at the time you finalize your order.
            </Box>
          )}
        >
          <HelpIcon sx={styles.helpIcon} />
        </WideLightTooltip>
      </Box>
    );
  };

  const handleOpenPopover = (e: React.MouseEvent<SVGSVGElement>) => {
    setAnchorEl(e.currentTarget as unknown as HTMLElement);
    setShowKmlMismatchPopover(true);
  };

  const handleClosePopover = () => {
    setShowKmlMismatchPopover(false);
    setAnchorEl(null);
  };

  const renderKmlMismatchPopover = (open: boolean) => (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClosePopover}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
    >
      <Box sx={styles.kmlMismatchContent}>
        <Typography variant="body2" sx={styles.kmlMismatchText}>
          The uploaded KML must intersect with the project area to proceed with order submission.
        </Typography>
      </Box>
    </Popover>
  )

  return (
    <>
      <ListItemButton sx={styles.expansionPanelHeader}>
        <Typography variant="h5">PROCESSING AREA</Typography>
        {activeKmlUrl && (
          <IconButton
            aria-label="Download .kml"
            sx={styles.button}
            onClick={KmlDownloadClick}
          >
            <img src={downloadIcon} alt="Download .kml" />
          </IconButton>
        )}
        <IconButton aria-label={kmlExpanded ? 'Collapse' : 'Expand'} sx={styles.button} onClick={toggleKmlExpanded}>
          {kmlExpanded
            ? <KeyboardArrowUpIcon fontSize="small" />
            : <KeyboardArrowDownIcon fontSize="small" />}
        </IconButton>
      </ListItemButton>
      <Collapse in={kmlExpanded}>
        <Box sx={styles.kmlFiles}>
          {nonActiveOrdersKmls.map((kml) => (
            <Box sx={styles.kmlFileRow} key={kml.name}>
              <Box sx={styles.kmlNameContainer}>
                {kml.kmlMismatch && <ErrorIcon sx={styles.errorIcon} fontSize='small' onClick={handleOpenPopover} />}
                {renderKmlMismatchPopover(showKmlMismatchPopover)}
                <Box sx={styles.kmlDot} />
                <Typography variant="body2" sx={styles.kmlHideButton}>{kml.name}</Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => dispatch(ToggleKmlVisibilityAction(kml.id))}
                aria-label="Toggle visibility"
              >
                {kmls[kml.id]?.visible
                  ? <VisibilityIcon sx={styles.visibilityIcon} fontSize="small" />
                  : <VisibilityOffIcon sx={styles.visibilityIcon} fontSize="small" />}
              </IconButton>
            </Box>
          ))}
          {activeOrderKml ?
            (
              <>
                <Box sx={styles.kmlFileRow}>
                  <Box sx={styles.kmlNameContainer}>
                    {activeOrderKml.kmlMismatch && <ErrorIcon sx={styles.errorIcon} fontSize='small' onClick={handleOpenPopover} />}
                    {renderKmlMismatchPopover(showKmlMismatchPopover)}
                    <Box sx={styles.kmlDotActive} />
                    <Typography variant="body2" sx={styles.kmlHideButton}>{activeOrderKml.name}</Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => dispatch(ToggleKmlVisibilityAction(activeOrderKml.id))}
                    aria-label="Toggle visibility"
                  >
                    {kmls[activeOrderKml.id]?.visible
                      ? <VisibilityIcon sx={styles.visibilityIcon} fontSize="small" />
                      : <VisibilityOffIcon sx={styles.visibilityIcon} fontSize="small" />}
                  </IconButton>
                </Box>
                {renderBusinessDays()}
              </>
            )
            : null}
        </Box>
        <Box sx={styles.kmlTabsWrapper}>
          <Box sx={styles.actionButtons}>
            <Button
              variant="outlined"
              sx={[styles.pillButton, tabValue === 'draw' && styles.pillButtonActive]}
              startIcon={<GestureIcon />}
              onClick={drawKmlClick}
              disabled={imageryRequested || rasterizationDisable}
            >
              Draw
            </Button>
            <Button
              variant="outlined"
              sx={[styles.pillButton, tabValue === 'upload' && styles.pillButtonActive]}
              startIcon={<SVG src={uploadKmlIcon} css={styles.tabIcon} />}
              onClick={(e) => uploadKmlClick(e)}
              disabled={imageryRequested || rasterizationDisable}
            >
              Upload
            </Button>
          </Box>
          {activeKmlUrl && activeOrderKml.geomType === 'Polygon' && !activeKmlUrl.includes('road_network_buffered.kml') && (
            <Button
              aria-label="Get Road Network"
              sx={styles.button}
              onClick={handleGenerateRoadNetwork}
            >
              <img src={roadNetworkIcon} alt="Get Road Network" />
              <span>&nbsp;Get Road Network</span>
            </Button>
          )}
        </Box>
      </Collapse>
      <ConfirmDialog
        showDialog={showConfirmDialog}
        onOk={onOk}
        onCancel={onCancel}
        contentText="Current .kml file will be removed"
      />
      <ConfirmProceedDialog
        title="Deleting the KML will modify your saved estimate."
        contentText="Are you sure you want to proceed?"
        showDialog={showConfirmProceedDialog}
        cancelButtonText="CANCEL"
        okButtonText="PROCEED"
        onOk={onOk}
        onCancel={() => setShowConfirmProceedDialog(false)}
      />
    </>
  );
};

export default KmlPanel;
